import React, { useEffect } from "react";
import { FormikConfig } from "formik";
import { useTranslation } from "react-i18next";
import { history } from "src/services/history";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { CloneForm } from "src/page-components/cabinet/my-clones/CloneSettings/CloneForm";
import { myClonesTrns } from "src/page-components/cabinet/my-clones/trns";
import { ICloneSettingsFormValues } from "src/page-components/cabinet/my-clones/CloneSettings/types";
import { EDIT_CLONE_VALIDATION_SCHEMA } from "src/page-components/cabinet/my-clones/CloneSettings/constants/validation";
import { getEditCloneInitialValues } from "src/page-components/cabinet/my-clones/CloneSettings/utils/getEditCloneInitialValues";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { scrollTop } from "src/utils/scrollTop";
import { selectCurrentUser } from "src/redux/currentUser/selector";
import { getCurrentCloneAsync } from "src/redux/currentClone/actions";
import { editCloneAsync } from "src/redux/myClones/actions";
import { selectorCurrentClone } from "src/redux/currentClone/selector";
import { getItemPath } from "src/utils/getItemPath";
import { PATHNAMES } from "src/constants/routes";
import { INITIAL_VALUES } from "../create";
import { useExtractId } from "src/hooks/useExractId";
import { combineSlugId } from "src/utils/combineSlugId";
import { MetadataWrapper } from "src/components/MetadataWrapper";
import { ScreenLoader } from "src/components/Loader/ScreenLoader";

const MyClonesEdit = () => {
  const id = useExtractId();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { item: clone, isLoading } = useAppSelector(selectorCurrentClone);
  const user = useAppSelector(selectCurrentUser);

  useEffect(() => {
    dispatch(getCurrentCloneAsync({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (clone && user) {
      const isOwner = user.host.email === clone.host.email;
      if (!isOwner) history.push(PATHNAMES.HOME, { replace: true });
    }
  }, [clone, user]);

  if (isLoading) return <ScreenLoader />;
  if (!clone) return null;

  const formikProps: FormikConfig<ICloneSettingsFormValues> = {
    initialValues: getEditCloneInitialValues(clone),
    validationSchema: EDIT_CLONE_VALIDATION_SCHEMA,
    onSubmit: (values, { resetForm }) => {
      const onSuccess = () => {
        const combinedSlug = combineSlugId(clone.slug, clone.id);
        history.push(
          getItemPath(PATHNAMES.CLONE_ITEM, { slug: combinedSlug }),
          {
            replace: true,
          }
        );
        resetForm({ values: INITIAL_VALUES });
        scrollTop();
      };

      dispatch(editCloneAsync({ values, id, onSuccess }));
    },
  };

  return (
    <MetadataWrapper
      title={clone?.clone_meta_title}
      description={clone?.clone_meta_description}
    >
      <PageWrapper>
        <section className="container py-25">
          <CloneForm
            firstTitle={t(myClonesTrns.doYouWantChangeCloneTypeTitle)}
            formikProps={formikProps}
            secondTitle={t(
              myClonesTrns.changeInformationAboutYourBusinessTitle
            )}
          />
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default MyClonesEdit;
